/**
 * Pivotree brand tokens consumed by the Backstage theme.
 *
 * This is the subset of `DESIGN.md` (repo root, the brand source of truth)
 * that the developer portal actually uses. Values are copied verbatim from
 * `DESIGN.md`; the comment after each names the originating token path so the
 * mapping back to the brand file is auditable. Do NOT invent values here - if
 * a token is missing, add it to `DESIGN.md` first (via `/design-init`) and then
 * mirror it.
 *
 * Only the product-chrome-relevant slice is mirrored. Commerce-only tokens
 * (CTA orange buttons, ProductCard, etc.) are intentionally omitted - they do
 * not apply to an internal portal.
 */

export const colors = {
  // Brand - Primary (navy workhorse)
  brandPrimaryPure: '#00205C', // colors.brand-primary-pure
  brandPrimaryDark: '#1F223D', // colors.brand-primary-dark
  brandPrimaryMedium: '#808FAD', // colors.brand-primary-medium
  brandPrimaryLight: '#EAEAF0', // colors.brand-primary-light

  // Brand - Secondary (green; primary buttons & confirmation)
  brandSecondaryPure: '#3C8064', // colors.brand-secondary-pure
  brandSecondaryDark: '#214536', // colors.brand-secondary-dark
  brandSecondaryMedium: '#77A693', // colors.brand-secondary-medium
  brandSecondaryLight: '#C5D9D1', // colors.brand-secondary-light

  // Highlight - reserved CTA orange (also used for *-warning feedback per DESIGN.md)
  highlightPure: '#FB8200', // colors.highlight-pure
  highlightDark: '#A45C0E', // colors.highlight-dark
  highlightMedium: '#FCA74D', // colors.highlight-medium
  highlightLight: '#FBF0E6', // colors.highlight-light

  // Neutrals - Low (text / foreground)
  neutralLowPure: '#333333', // colors.neutral-low-pure
  neutralLowLight: '#575757', // colors.neutral-low-light

  // Neutrals - High (surfaces / background)
  neutralHighPure: '#FFFFFF', // colors.neutral-high-pure
  neutralHighLight: '#F9F9F9', // colors.neutral-high-light
  neutralHighMedium: '#EAEAF0', // colors.neutral-high-medium
  neutralHighDark: '#B2BCCA', // colors.neutral-high-dark

  // Feedback - Warning / Danger (red)
  feedbackWarningPure: '#D25A5B', // colors.feedback-warning-pure
  feedbackWarningDark: '#782122', // colors.feedback-warning-dark
  feedbackWarningMedium: '#E49C9D', // colors.feedback-warning-medium
  feedbackWarningLight: '#EDBDBD', // colors.feedback-warning-light

  // Feedback - Success (distinct from brand green)
  feedbackSuccessPure: '#1F7A4D', // colors.feedback-success-pure
  feedbackSuccessDark: '#145235', // colors.feedback-success-dark
  feedbackSuccessMedium: '#6AB88E', // colors.feedback-success-medium
  feedbackSuccessLight: '#CCE9DC', // colors.feedback-success-light

  // Feedback - Info
  feedbackInfoPure: '#1363C3', // colors.feedback-info-pure
  feedbackInfoDark: '#0D407F', // colors.feedback-info-dark
  feedbackInfoMedium: '#4F8EDC', // colors.feedback-info-medium
  feedbackInfoLight: '#D4E5F8', // colors.feedback-info-light
} as const;

/** Corner radii - `rounded.*` in DESIGN.md. */
export const rounded = {
  none: 0, // rounded.none
  sm: 5, // rounded.sm  - inputs, small chips
  md: 8, // rounded.md  - buttons, cards-in-cards, tags (workhorse)
  lg: 16, // rounded.lg  - cards, modals
  pill: 500, // rounded.pill - status pills, switches
} as const;

/**
 * Soft, low-contrast elevation - all built on rgba(0,0,0,0.08).
 * "Elevation & Depth" in DESIGN.md. Never stack more than two levels.
 */
export const elevation = {
  level1: '0 4px 8px rgba(0, 0, 0, 0.08)', // cards, buttons at rest
  level2: '0 8px 24px rgba(0, 0, 0, 0.08)', // hovered cards, dropdowns
  level3: '0 16px 32px rgba(0, 0, 0, 0.08)', // modals, popovers
  level4: '0 16px 48px rgba(0, 0, 0, 0.08)', // full-screen overlays
} as const;

/**
 * Type ramp from DESIGN.md `typography.*`.
 *
 * NOTE: DESIGN.md specifies Montserrat (UI) and Domine (editorial), self-hosted
 * from `/fonts/` - never from a CDN. Those font files are not yet vendored in
 * this repo, so `fontFamily` is intentionally left as the Backstage default.
 * The size/weight/line-height ramp below is still applied for correct hierarchy.
 *
 * TODO(brand-fonts): self-host Montserrat + Domine under packages/app and set
 * `fontFamily: 'Montserrat'` here. Do NOT load from Google Fonts / any CDN.
 */
export const typography = {
  htmlFontSize: 16,
  // role: [fontSize px, fontWeight, lineHeight, letterSpacing]
  h1: { fontSize: 32, fontWeight: 700, lineHeight: 1.1, letterSpacing: '-0.02em' }, // typography.h1
  h2: { fontSize: 28, fontWeight: 700, lineHeight: 1.1, letterSpacing: '-0.01em' }, // typography.h2
  h3: { fontSize: 24, fontWeight: 600, lineHeight: 1.25 }, // typography.h3
  h4: { fontSize: 20, fontWeight: 600, lineHeight: 1.25 }, // typography.h4
  h5: { fontSize: 18, fontWeight: 600, lineHeight: 1.5 }, // typography.h5
  h6: { fontSize: 16, fontWeight: 600, lineHeight: 1.5 }, // typography.h6
  bodyMd: { fontSize: 16, fontWeight: 400, lineHeight: 1.5 }, // typography.body-md
  bodySm: { fontSize: 14, fontWeight: 400, lineHeight: 1.5 }, // typography.body-sm
  caption: { fontSize: 12, fontWeight: 500, lineHeight: 1.5, letterSpacing: '0.01em' }, // typography.caption
} as const;
